<section class="user-edit-page">
  <header class="user-edit-page__header">
    <div class="user-edit-page__header-inner">
      <div class="user-edit-page__header-left">
        <button
          type="button"
          class="user-edit-page__back"
          (click)="navigateBack()"
          [attr.aria-label]="'userEdit.header.back' | translate"
        >
          <mat-icon aria-hidden="true">arrow_back</mat-icon>
        </button>
        <div class="user-edit-page__header-text">
          <div class="user-edit-page__eyebrow">{{ 'userEdit.header.eyebrow' | translate }}</div>
          <h1 class="user-edit-page__title">
            {{ displayName() || ('userEdit.header.fallbackName' | translate) }}
          </h1>
        </div>
        @if (hasUnsavedChanges) {
          <span class="user-edit-page__dirty-pill" role="status" aria-live="polite">
            <span class="user-edit-page__dirty-dot" aria-hidden="true"></span>
            {{ 'userEdit.stammdaten.unsavedChanges' | translate }}
          </span>
        }
      </div>
      <div class="user-edit-page__header-actions">
        <button type="button" class="user-edit-page__cancel" (click)="navigateBack()">
          {{ 'userEdit.header.cancel' | translate }}
        </button>
        <button type="button" class="user-edit-page__save" [disabled]="!canSave" (click)="onSave()">
          <mat-icon aria-hidden="true">check</mat-icon>
          <span>{{ 'userEdit.header.save' | translate }}</span>
        </button>
      </div>
    </div>
  </header>

  <div class="user-edit-page__body">
    @if (user(); as currentUser) {
      <article class="user-edit-page__preview-card" aria-labelledby="user-edit-preview-title">
        <app-avatar [name]="displayName()" [size]="68" />
        <div class="user-edit-page__preview-text">
          <div class="user-edit-page__eyebrow">{{ 'userEdit.preview.label' | translate }}</div>
          <div id="user-edit-preview-title" class="user-edit-page__preview-name">{{ displayName() }}</div>
          <div class="user-edit-page__preview-meta">
            <span class="user-edit-page__preview-email">{{ email() }}</span>
            <span class="user-edit-page__preview-dot" aria-hidden="true"></span>
            <span class="user-edit-page__role-pill">{{ 'roles.' + currentUser.role | translate }}</span>
            <span class="user-edit-page__preview-dot" aria-hidden="true"></span>
            <app-status-pill [kind]="statusPillKind()" size="sm" />
          </div>
        </div>
      </article>
    }

    @if (error(); as errorKey) {
      <div class="user-edit-page__info-banner user-edit-page__info-banner--error" role="alert">
        <mat-icon aria-hidden="true">error_outline</mat-icon>
        <span>{{ errorKey | translate }}</span>
      </div>
    }

    <div class="user-edit-page__tabs" role="tablist" [attr.aria-label]="'userEdit.tabs.ariaLabel' | translate">
      @for (tab of tabs; track tab.id; let i = $index) {
        <button
          type="button"
          role="tab"
          class="user-edit-page__tab"
          [class.user-edit-page__tab--active]="activeTab() === tab.id"
          [attr.aria-selected]="activeTab() === tab.id"
          [attr.tabindex]="activeTab() === tab.id ? 0 : -1"
          [attr.id]="'user-edit-tab-' + tab.id"
          [attr.aria-controls]="'user-edit-panel-' + tab.id"
          (click)="setActiveTab(tab.id)"
          (keydown)="onTabKeydown($event, i)"
        >
          <mat-icon aria-hidden="true">{{ tab.icon }}</mat-icon>
          <span>{{ tab.labelKey | translate }}</span>
        </button>
      }
    </div>

    <form [formGroup]="form" (ngSubmit)="onSave()" novalidate>
      @if (activeTab() === 'daten') {
        <section
          class="user-edit-page__panel"
          role="tabpanel"
          id="user-edit-panel-daten"
          aria-labelledby="user-edit-tab-daten"
        >
          <to-section-title
            [title]="'userEdit.stammdaten.title' | translate"
            [subtitle]="'userEdit.stammdaten.subtitle' | translate"
          />

          @if (masterDataReadOnly()) {
            <div class="user-edit-page__info-banner" role="note">
              <mat-icon aria-hidden="true">lock</mat-icon>
              <span>{{ 'userEdit.stammdaten.masterDataReadOnly' | translate }}</span>
            </div>
          }

          @if (showInvitationInfo()) {
            <div class="user-edit-page__info-banner user-edit-page__info-banner--invitation" role="note">
              <mat-icon aria-hidden="true">{{ invitationExpired() ? 'error_outline' : 'schedule' }}</mat-icon>
              <div class="user-edit-page__invitation-info-text">
                @if (invitationExpired()) {
                  <span class="user-edit-page__invitation-status user-edit-page__invitation-status--expired">
                    {{ 'userEdit.invitation.expired' | translate }}
                  </span>
                } @else if (user()?.remainingDays !== undefined && user()?.remainingDays !== null) {
                  <span class="user-edit-page__invitation-status">
                    {{ 'userEdit.invitation.remaining' | translate: { days: user()!.remainingDays } }}
                  </span>
                }
                @if (user()?.sentAt) {
                  <span class="user-edit-page__invitation-sent">
                    {{ 'userEdit.invitation.sent' | translate: { date: (user()!.sentAt | date: 'dd.MM.yyyy') } }}
                  </span>
                }
              </div>
              <div aria-live="polite" aria-atomic="true">
                @if (invitationResent()) {
                  <span class="user-edit-page__invitation-confirm">{{ 'userEdit.invitation.resent' | translate }}</span>
                } @else {
                  <button
                    type="button"
                    class="user-edit-page__invitation-resend-btn"
                    [disabled]="invitationResending()"
                    (click)="onResendInvitation()"
                  >
                    <mat-icon aria-hidden="true">autorenew</mat-icon>
                    <span>{{ 'userEdit.invitation.resend' | translate }}</span>
                  </button>
                }
              </div>
            </div>
          }

          <div class="user-edit-page__form-grid">
            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.stammdaten.firstNameLabel' | translate }}</mat-label>
              <input
                matInput
                formControlName="firstName"
                [attr.maxlength]="fieldLengths.FIRST_LAST_NAME_MAX"
                required
              />
              @if (form.controls.firstName.hasError('required') && form.controls.firstName.touched) {
                <mat-error>{{ 'userEdit.stammdaten.firstNameRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.stammdaten.lastNameLabel' | translate }}</mat-label>
              <input matInput formControlName="lastName" [attr.maxlength]="fieldLengths.FIRST_LAST_NAME_MAX" required />
              @if (form.controls.lastName.hasError('required') && form.controls.lastName.touched) {
                <mat-error>{{ 'userEdit.stammdaten.lastNameRequired' | translate }}</mat-error>
              }
            </mat-form-field>

            @if (!isAgent()) {
              <mat-form-field appearance="fill" class="user-edit-page__form-field user-edit-page__form-field--full">
                <mat-label>{{ 'userEdit.stammdaten.emailLabel' | translate }}</mat-label>
                @if (showInvitationInfo()) {
                  <input matInput [value]="email()" readonly aria-readonly="true" />
                  <mat-hint>{{ 'userEdit.stammdaten.emailHint' | translate }}</mat-hint>
                } @else {
                  <input
                    #emailInput
                    matInput
                    type="email"
                    formControlName="email"
                    [attr.maxlength]="fieldLengths.EMAIL_MAX"
                    [placeholder]="'userEdit.stammdaten.emailPlaceholder' | translate"
                    required
                  />
                  @if (form.controls.email.hasError('required')) {
                    <mat-error>{{ 'userEdit.stammdaten.emailRequired' | translate }}</mat-error>
                  } @else if (form.controls.email.hasError('email')) {
                    <mat-error>{{ 'userEdit.stammdaten.emailInvalid' | translate }}</mat-error>
                  } @else if (form.controls.email.hasError('emailInUse')) {
                    <mat-error>{{ 'userEdit.stammdaten.emailInUse' | translate }}</mat-error>
                  } @else if (form.controls.email.hasError('domainNotAllowed')) {
                    <mat-error>
                      @if (emailAllowedDomainsLabel()) {
                        {{
                          'userEdit.stammdaten.emailDomainNotAllowed'
                            | translate: { domains: emailAllowedDomainsLabel() }
                        }}
                      } @else {
                        {{ 'userEdit.stammdaten.emailDomainNoneConfigured' | translate }}
                      }
                    </mat-error>
                  }
                  <mat-hint>{{ 'userEdit.stammdaten.emailLoginHint' | translate }}</mat-hint>
                }
              </mat-form-field>
            }

            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.stammdaten.roleLabel' | translate }}</mat-label>
              <mat-select formControlName="roleId">
                @for (role of roleOptions(); track role.id) {
                  @let lockReason = roleLockReason(role);
                  <mat-option
                    [value]="role.id"
                    [disabled]="!!lockReason"
                    [matTooltip]="lockReason ? (lockReason | translate) : null"
                  >
                    {{ role.displayName }}
                  </mat-option>
                }
              </mat-select>
              @if (tenantRoleLock(); as lock) {
                <mat-hint>{{ 'userEdit.stammdaten.roleLocked.' + lock | translate }}</mat-hint>
              }
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.stammdaten.statusLabel' | translate }}</mat-label>
              <mat-select formControlName="status">
                @for (status of statusOptions; track status) {
                  <mat-option [value]="status">
                    {{ 'userEdit.stammdaten.statusValues.' + status | translate }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            @if (tenantRoleOverrideHintKey(); as hintKey) {
              <p class="user-edit-page__override-hint user-edit-page__form-field--full" role="status">
                {{ hintKey | translate: { name: displayName() } }}
              </p>
            }
          </div>
        </section>
      }

      @if (activeTab() === 'rolle' && !error()) {
        <section
          class="user-edit-page__panel"
          role="tabpanel"
          id="user-edit-panel-rolle"
          aria-labelledby="user-edit-tab-rolle"
        >
          <to-section-title
            [title]="'userEdit.berechtigungen.title' | translate"
            [subtitle]="'userEdit.berechtigungen.subtitle' | translate"
          />

          <app-einzelrechte-matrix
            [data]="permissions()"
            [state]="permissionsState()"
            [userName]="displayName()"
            [tenantRoleLabel]="'roles.' + (user()?.role ?? '') | translate"
            [(selectedProjectId)]="selectedPermissionProject"
            [(checked)]="checkedPermissions"
            [disabled]="saving()"
          />
        </section>
      }

      @if (activeTab() === 'praeferenzen') {
        <section
          class="user-edit-page__panel"
          role="tabpanel"
          id="user-edit-panel-praeferenzen"
          aria-labelledby="user-edit-tab-praeferenzen"
        >
          <to-section-title
            [title]="'userEdit.preferences.title' | translate"
            [subtitle]="'userEdit.preferences.subtitle' | translate"
          />

          @if (masterDataReadOnly()) {
            <div class="user-edit-page__info-banner" role="note">
              <mat-icon aria-hidden="true">lock</mat-icon>
              <span>{{ 'userEdit.stammdaten.masterDataReadOnly' | translate }}</span>
            </div>
          }

          <div class="user-edit-page__form-grid">
            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.preferences.languageLabel' | translate }}</mat-label>
              <mat-select formControlName="language">
                @for (lang of languageOptions(); track lang.code) {
                  <mat-option [value]="lang.code">{{ lang.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="fill" class="user-edit-page__form-field">
              <mat-label>{{ 'userEdit.preferences.timezoneLabel' | translate }}</mat-label>
              <mat-select formControlName="timezone">
                @for (tz of timezoneOptions(); track tz) {
                  <mat-option [value]="tz">{{ tz }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          @if (isPlatformAdmin()) {
            <div class="user-edit-page__danger-zone">
              <div class="user-edit-page__danger-label">{{ 'userEdit.dangerZone.label' | translate }}</div>
              <div class="user-edit-page__danger-actions">
                <button
                  type="button"
                  class="user-edit-page__danger-btn user-edit-page__danger-btn--danger user-edit-page__btn-delete"
                  (click)="openDeleteDialog()"
                >
                  <mat-icon aria-hidden="true">delete</mat-icon>
                  <span>{{ 'userEdit.dangerZone.deleteUser' | translate }}</span>
                </button>
              </div>
            </div>
          }
        </section>
      }
    </form>
  </div>
</section>

<ng-template #deleteConfirmTemplate>
  <div class="user-edit-page__delete-dialog">
    <p class="user-edit-page__delete-message">
      {{ 'userEdit.delete.confirmMessage' | translate: { name: displayName() } }}
    </p>
    @if (blockingProjects().length > 0) {
      <div class="user-edit-page__blocking" role="alert">
        <p class="user-edit-page__blocking-label">{{ 'userEdit.delete.blockingProjectsLabel' | translate }}</p>
        <ul class="user-edit-page__blocking-list">
          @for (project of blockingProjects(); track project.id) {
            <li>{{ project.name }}</li>
          }
        </ul>
      </div>
    }
  </div>
</ng-template>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="showDeleteDialog()"
  [title]="'userEdit.delete.confirmTitle' | translate"
  [contentTemplate]="deleteConfirmTemplate"
  [saveText]="'userEdit.delete.confirmButton' | translate"
  [cancelText]="'userEdit.delete.cancelButton' | translate"
  [disabled]="deleting() || blockingProjects().length > 0"
  (save)="onDeleteConfirmed()"
  (cancel)="onDeleteCancelled()"
  (visibleChange)="!$event && onDeleteCancelled()"
></lib-modal>

<lib-modal
  [closeLabel]="'general.close' | translate"
  [title]="'common.unsavedChanges.title' | translate"
  [text]="'common.unsavedChanges.message' | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
